import { useState, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Gift, X, Sparkles } from "lucide-react";

const WHEEL_COLORS = ["#DC2626", "#F59E0B", "#10B981", "#3B82F6", "#8B5CF6", "#EC4899", "#0EA5E9", "#F97316", "#14B8A6", "#84CC16", "#6366F1", "#D946EF", "#EAB308"];
const money = (n) => `$${Number(n || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const short = (s, n = 15) => (s.length > n ? s.slice(0, n - 1) + "…" : s);

function FloatBtn({ children, onClick, className = "", testid, label }) {
  return <button data-testid={testid} aria-label={label} onClick={onClick} className={`w-11 h-11 grid place-items-center rounded-full shadow-md transition-colors text-white ${className}`}>{children}</button>;
}

// Widget autónomo: se fija solo si hay una ruleta activa que este cliente
// todavía no gira, y no renderiza nada si no. onWon se llama después de que
// se anima el premio, para que quien lo use (Store.jsx) refresque saldo,
// compras y notificaciones sin tener que saber nada de cómo funciona la ruleta.
export default function RouletteWheel({ onWon }) {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const reduced = useReducedMotion();

  useEffect(() => { api.get("/roulette/status").then((r) => setStatus(r.data)).catch(() => {}); }, []);

  if (!status?.active || status.ya_giro) return null;

  const n = status.prizes.length;
  const seg = 360 / n;
  const spinDuration = reduced ? 0.6 : 5;
  const firstName = (user?.name || "").split(" ")[0];

  const spin = async () => {
    if (spinning) return;
    setSpinning(true); setError("");
    try {
      const { data } = await api.post("/roulette/spin");
      const idx = status.prizes.findIndex((p) => p.id === data.prize.id);
      const center = idx * seg + seg / 2;
      const fullSpins = reduced ? 1 : 6;
      setRotation(360 * fullSpins - center);
      setTimeout(() => {
        setResult({ ...data.prize, sin_stock: data.sin_stock });
        setSpinning(false);
        setStatus((s) => ({ ...s, ya_giro: true, cupo_restante: data.cupo_restante }));
        toast.success(`🎉 ¡Felicidades${firstName ? `, ${firstName}` : ""}! Ganaste: ${data.prize.label}`, { duration: 6000 });
        onWon?.(data.prize, data.compra);
        // Cierra el modal solo -- así lo que queda a la vista es el banner
        // verde de "compra realizada" (para el premio de producto), igual
        // que después de una compra normal. Si se quedó sin stock, se deja
        // el modal abierto más tiempo para que el mensaje de "te la
        // entregamos en breve" no se pierda de vista.
        setTimeout(() => setOpen(false), data.sin_stock ? 4500 : 1800);
      }, spinDuration * 1000 + 150);
    } catch (e) {
      setError(apiError(e.response?.data?.detail));
      setSpinning(false);
    }
  };

  const wheelSize = 260;
  const center = wheelSize / 2;
  const labelRadius = center * 0.62;

  return (
    <>
      <div className="relative">
        <FloatBtn testid="roulette-fab" label="Ruleta de premios" onClick={() => setOpen(true)}
          className="bg-gradient-to-br from-amber-400 to-primary hover:brightness-110">
          <Gift size={18} />
        </FloatBtn>
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-amber-400 border-2 border-white animate-pulse" />
      </div>

      {open && (
        <div role="dialog" aria-modal="true" aria-label="Ruleta de premios" className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !spinning && setOpen(false)}>
          <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-3xl max-w-sm w-full p-6 text-center shadow-md animate-fade-up">
            {!spinning && (
              <button onClick={() => setOpen(false)} aria-label="Cerrar" className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full bg-slate-100 hover:bg-slate-200 text-slate-500"><X size={16} /></button>
            )}

            {!result ? (
              <>
                <h3 className="font-head text-xl font-extrabold text-slate-900 flex items-center justify-center gap-2"><Gift className="text-primary" size={22} /> Ruleta de Premios</h3>
                <p className="text-xs text-slate-500 mt-1 mb-5">Un giro por persona. ¡Mucha suerte!</p>

                <div className="relative mx-auto" style={{ width: wheelSize, height: wheelSize }}>
                  <div className="absolute left-1/2 -translate-x-1/2 -top-1 z-10"
                    style={{ width: 0, height: 0, borderLeft: "11px solid transparent", borderRight: "11px solid transparent", borderTop: "17px solid #0f172a" }} />
                  <motion.div
                    animate={{ rotate: rotation }}
                    transition={{ duration: spinDuration, ease: [0.12, 0.8, 0.2, 1] }}
                    className="w-full h-full rounded-full border-[6px] border-slate-900 relative shadow-inner"
                    style={{ background: `conic-gradient(${status.prizes.map((p, i) => `${WHEEL_COLORS[i % WHEEL_COLORS.length]} ${i * seg}deg ${(i + 1) * seg}deg`).join(", ")})` }}
                  >
                    {status.prizes.map((p, i) => {
                      const angle = i * seg + seg / 2;
                      const rad = ((angle - 90) * Math.PI) / 180;
                      const x = center + labelRadius * Math.cos(rad);
                      const y = center + labelRadius * Math.sin(rad);
                      // Con foto del producto: miniatura redonda, siempre derecha
                      // (no gira con el gajo, para que la foto se vea bien). Sin
                      // foto (premios de saldo, o sin imagen en el catálogo): el
                      // nombre corto de siempre.
                      return p.image ? (
                        <div key={p.id} className="absolute rounded-full overflow-hidden border-2 border-white shadow-md pointer-events-none bg-white"
                          style={{ left: x, top: y, width: 36, height: 36, transform: "translate(-50%, -50%)" }}>
                          <img src={p.image} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : (
                        <span key={p.id} className="absolute text-[9px] font-bold text-white text-center leading-tight drop-shadow pointer-events-none"
                          style={{ left: x, top: y, width: 64, transform: `translate(-50%, -50%) rotate(${angle}deg)` }}>
                          {short(p.label)}
                        </span>
                      );
                    })}
                  </motion.div>
                  <div className="absolute inset-0 m-auto w-11 h-11 rounded-full bg-white border-[5px] border-slate-900 grid place-items-center z-10">
                    <Sparkles size={16} className="text-primary" />
                  </div>
                </div>

                {error && <p className="text-red-600 text-sm font-semibold mt-3">{error}</p>}
                <button data-testid="roulette-spin-btn" onClick={spin} disabled={spinning}
                  className="mt-5 w-full bg-primary hover:bg-primary-hover disabled:opacity-60 text-white font-bold rounded-lg py-3 transition-colors">
                  {spinning ? "Girando..." : "¡Girar ahora!"}
                </button>
              </>
            ) : (
              <div className="py-4">
                <p className="text-5xl mb-3">🎉</p>
                <h3 className="font-head text-xl font-extrabold text-slate-900">¡Ganaste!</h3>
                <p className="text-primary font-bold text-lg mt-2">{result.label}</p>
                {result.tipo === "saldo" && <p className="text-emerald-600 font-semibold text-sm mt-1">+{money(result.valor)} ya agregado a tu saldo</p>}
                {result.tipo === "producto" && !result.sin_stock && <p className="text-slate-500 text-sm mt-1">Ya quedó en tu historial de compras.</p>}
                {result.tipo === "producto" && result.sin_stock && (
                  <p className="text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm mt-3">
                    Justo se agotó el stock de este premio -- no te preocupes, te la entregamos en breve. Un administrador ya fue avisado.
                  </p>
                )}
                <button onClick={() => setOpen(false)} className="mt-6 w-full bg-primary hover:bg-primary-hover text-white font-semibold rounded-lg py-3 transition-colors">Cerrar</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
